import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import CourseCardSkeleton from '@/components/CourseCardSkeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BookOpen, Play, Award, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function MyCourses({ user, onLogout }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      // OPTIMIZED: Single API call instead of 100+ calls
      const res = await axios.get(`${API}/my-courses`, { headers: getAuthHeaders() });
      setCourses(res.data.map(course => ({
        ...course,
        firstUncompletedLessonId: course.first_uncompleted_lesson_id,
        isCompleted: course.is_completed
      })));
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="courses">
        <div className="p-6 lg:p-8 space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-navy">My Courses</h1>
            <p className="text-[#4D4D4D] mt-2 font-light">Loading your courses...</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => <CourseCardSkeleton key={i} />)}
          </div>
        </div>
      </Layout>
    );
  }

  // Separate ongoing and completed courses
  const ongoingCourses = courses.filter(course => !course.isCompleted);
  const completedCourses = courses.filter(course => course.isCompleted);

  const calculateDaysLeft = (course) => {
    if (!course.duration_value || !course.duration_unit) return null;
    
    // Convert duration to days
    let totalDays = course.duration_value;
    if (course.duration_unit === 'weeks') {
      totalDays = course.duration_value * 7;
    } else if (course.duration_unit === 'months') {
      totalDays = course.duration_value * 30;
    }
    
    // Get or set assignment date for this course
    const storageKey = `course_assignment_${course.id}_${user.id}`;
    let assignmentDate = localStorage.getItem(storageKey);
    
    if (!assignmentDate) {
      // First time accessing - set assignment date to today
      assignmentDate = new Date().toISOString();
      localStorage.setItem(storageKey, assignmentDate);
    }
    
    // Calculate days elapsed since assignment
    const assigned = new Date(assignmentDate);
    const today = new Date();
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysElapsed = Math.floor((today - assigned) / msPerDay);
    
    // Calculate days left
    const daysLeft = totalDays - daysElapsed;
    
    return Math.max(0, daysLeft);
  };

  const renderCourseCard = (course, isCompleted = false) => {
    const getThumbnailUrl = () => {
      if (!course.thumbnail_url) return null;
      if (course.thumbnail_url.startsWith('http')) return course.thumbnail_url;
      return `${BACKEND_URL}${course.thumbnail_url}`;
    };

    const daysLeft = calculateDaysLeft(course);

    return (
      <Card
        key={course.id}
        className={`border-0 card-shadow card-hover transition-smooth cursor-pointer rounded-2xl overflow-hidden ${
          isCompleted ? 'bg-green-50 border-2 border-green-200' : 'bg-white'
        }`}
        onClick={() => navigate(`/courses/${course.id}`)}
        data-testid={`course-card-${course.id}`}
      >
        {/* Course Thumbnail - Top 50% */}
        <div className="relative h-56 overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100">
          {course.thumbnail_url ? (
            <>
              <img src={getThumbnailUrl()} alt={course.title} className="w-full h-full object-cover" />
              {isCompleted && (
                <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                  <Award className="w-3 h-3" strokeWidth={2} />
                  <span>Completed</span>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <BookOpen className="w-20 h-20 text-primary-blue opacity-30" strokeWidth={1.5} />
            </div>
          )}
        </div>

        {/* Content Section - Bottom 50% */}
        <CardContent className="p-5 space-y-3">
          {/* Course Name */}
          <div className="flex items-start justify-between">
            <h3 className="text-lg font-bold text-navy line-clamp-2 flex-1">{course.title}</h3>
            {isCompleted && !course.thumbnail_url && (
              <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1 ml-2">
                <Award className="w-3 h-3" strokeWidth={2} />
                <span>Done</span>
              </div>
            )}
          </div>

          {/* Days Left - Only for ongoing courses */}
          {!isCompleted && daysLeft !== null && (
            <div className="flex items-center space-x-2 text-sm text-[#4D4D4D]">
              <span className="font-medium">{daysLeft} days left</span>
            </div>
          )}

          {/* Progress bar (for ongoing courses) or Action buttons (for completed) */}
          {!isCompleted ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#4D4D4D]">Progress</span>
                <span className="text-sm font-bold text-primary-blue" data-testid={`course-progress-${course.id}`}>{course.progress?.toFixed(0) || 0}%</span>
              </div>
              <div className="w-full bg-[#E7E7E7] rounded-full h-2 mb-4">
                <div className="premium-gradient h-2 rounded-full transition-smooth" style={{ width: `${course.progress || 0}%` }}></div>
              </div>
              
              {/* Continue Button */}
              {course.firstUncompletedLessonId && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/courses/${course.id}`);
                  }}
                  className="w-full bg-primary-blue hover:bg-[#1874CD] text-white font-semibold py-2 rounded-xl transition-smooth btn-press flex items-center justify-center space-x-2"
                >
                  <Play className="w-4 h-4" strokeWidth={2} />
                  <span>Continue</span>
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCertificateModal(true);
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-xl transition-smooth btn-press flex items-center justify-center space-x-2"
              >
                <Award className="w-4 h-4" strokeWidth={2} />
                <span>View Certificate</span>
              </Button>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/courses/${course.id}`);
                }}
                variant="outline"
                className="w-full border-2 border-green-500 text-green-700 hover:bg-green-50 font-semibold py-2 rounded-xl transition-smooth flex items-center justify-center space-x-2"
              >
                <RotateCcw className="w-4 h-4" strokeWidth={2} />
                <span>Rewatch Course</span>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout user={user} onLogout={onLogout} currentPage="courses">
      <div className="p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6" data-testid="student-courses">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-navy mb-2">My Courses</h1>
          <p className="text-sm md:text-base text-[#4D4D4D]">Continue your learning journey</p>
        </div>

        {courses.length === 0 ? (
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-16 text-center">
              <BookOpen className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
              <h3 className="text-xl font-bold text-navy mb-2">No Courses Yet</h3>
              <p className="text-[#4D4D4D] font-light">You haven't been assigned any courses yet. Contact your instructor.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Ongoing Courses */}
            {ongoingCourses.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {ongoingCourses.map((course) => renderCourseCard(course, false))}
              </div>
            )}

            {ongoingCourses.length === 0 && completedCourses.length > 0 && (
              <Card className="border-0 card-shadow bg-white rounded-2xl">
                <CardContent className="p-12 text-center">
                  <Award className="w-16 h-16 mx-auto mb-4 text-green-500" strokeWidth={1.5} />
                  <h3 className="text-xl font-bold text-navy mb-2">All Courses Completed!</h3>
                  <p className="text-[#4D4D4D] font-light">You've completed all your assigned courses. Great job!</p>
                </CardContent>
              </Card>
            )}

            {/* Completed Courses Section */}
            {completedCourses.length > 0 && (
              <div className="mt-8 md:mt-12">
                <div className="mb-4 md:mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-navy flex items-center space-x-2">
                    <Award className="w-6 h-6 text-green-500" strokeWidth={2} />
                    <span>My Completed Courses</span>
                  </h2>
                  <p className="text-[#4D4D4D] mt-2 font-light">Congratulations on completing these courses!</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {completedCourses.map((course) => renderCourseCard(course, true))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Certificate Coming Soon Modal */}
      <Dialog open={showCertificateModal} onOpenChange={setShowCertificateModal}>
        <DialogContent className="bg-white rounded-2xl shadow-2xl border-0 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-navy text-center flex items-center justify-center space-x-2 mb-4">
              <Award className="w-6 h-6 text-green-500" strokeWidth={2} />
              <span>Certificate Coming Soon</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="text-center space-y-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Award className="w-10 h-10 text-green-500" strokeWidth={2} />
            </div>
            
            <p className="text-[#4D4D4D] text-lg">
              Your Certificate is coming soon! Our team is preparing it for you.
            </p>
            
            <Button
              onClick={() => setShowCertificateModal(false)}
              className="w-full bg-primary-blue hover:bg-[#1874CD] text-white font-semibold py-3 rounded-xl transition-smooth btn-press"
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}